import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { PackageOpen, Clock } from "lucide-react";

// Only buyer-visible stages — internal stages (Ordered, Picked, Shipped) are hidden from buyer.
const STAGES = [
  { key: "inventory_ready", label: "Inventory Ready" },
  { key: "ready_for_dispatch", label: "Ready for Dispatch" },
  { key: "dispatched", label: "Dispatched" },
  { key: "delivered", label: "Delivered" },
];
const PRE_READY_STATUSES = new Set(["awaiting_procurement", "ordered", "picked", "shipped"]);

export default function YetToReceive() {
  const { customer } = useApp();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!customer) return;
    api.get(`/customers/${customer.id}/backorders`).then(r => setItems(r.data)).catch(() => setItems([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  if (!customer) return <div className="p-20 text-center text-[#8A8A8A]" data-testid="ytr-guest">Please register first.</div>;
  if (items === null) return <div className="p-20 text-center text-[#8A8A8A]" data-testid="ytr-loading">Loading your backorders…</div>;

  const activeItems = items.filter(it => it.procurement_status !== "delivered");
  const deliveredItems = items.filter(it => it.procurement_status === "delivered");

  return (
    <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16" data-testid="yet-to-receive-page">
      <div className="mb-12">
        <p className="label-eyebrow">Reserved · Paid · In Progress</p>
        <h1 className="font-editorial text-5xl sm:text-6xl tracking-editorial mt-3">Yet to Receive</h1>
        <p className="text-[#5C5C5C] mt-4 max-w-2xl">
          Every item here is paid for and being arranged for you. You&apos;ll see live status updates from our team — from procurement to delivery.
        </p>
      </div>

      {items.length === 0 && (
        <div className="border border-dashed border-[#1A1A1A]/15 p-20 text-center text-[#8A8A8A]" data-testid="ytr-empty">
          <PackageOpen className="w-10 h-10 mx-auto mb-4 text-[#C9A968]" strokeWidth={1.2} />
          <p className="font-editorial text-2xl mb-2 text-[#1A1A1A]">Nothing pending</p>
          <p className="text-sm">When you order items that need to be procured, they&apos;ll appear here with live status updates.</p>
          <Link to="/catalog" className="btn-outline-ink mt-6 inline-flex">Browse Catalog</Link>
        </div>
      )}

      {activeItems.length > 0 && (
        <section className="mb-16" data-testid="ytr-active">
          <p className="label-eyebrow mb-6">In progress · {activeItems.length}</p>
          <div className="space-y-6">
            {activeItems.map(it => <BackorderCard key={`${it.order_id}-${it.product_id}`} item={it} />)}
          </div>
        </section>
      )}

      {deliveredItems.length > 0 && (
        <section data-testid="ytr-delivered">
          <p className="label-eyebrow mb-6">Delivered · {deliveredItems.length}</p>
          <div className="space-y-6 opacity-70">
            {deliveredItems.map(it => <BackorderCard key={`${it.order_id}-${it.product_id}`} item={it} delivered />)}
          </div>
        </section>
      )}
    </div>
  );
}

function BackorderCard({ item, delivered }) {
  const ps = item.procurement_status || "awaiting_procurement";
  const preReady = PRE_READY_STATUSES.has(ps);
  const idx = preReady ? -1 : STAGES.findIndex(s => s.key === ps);
  const isReady = ps === "inventory_ready";
  return (
    <div className="border border-[#1A1A1A]/10 bg-white p-6" data-testid={`ytr-item-${item.our_code}`}>
      <div className="flex justify-between items-start flex-wrap gap-4 mb-5">
        <div>
          <p className="font-medium text-lg">{item.product_name}</p>
          <p className="text-xs text-[#8A8A8A] mt-1">
            Code {item.our_code} · {item.size} · {item.quantity_boxes} boxes ({item.packing}/box)
          </p>
          <p className="text-xs text-[#8A8A8A] mt-2">
            From order <Link to={`/orders/${item.order_id}`} className="text-[#1E2C5A] hover:underline">#{item.order_no}</Link>
            {item.lead_time_days ? <> · lead time ~<span className="font-medium">{item.lead_time_days} days</span></> : null}
          </p>
        </div>
        <div className="text-right">
          {preReady && !delivered && (
            <span className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-[#8B7A3E]">
              <Clock className="w-3 h-3" strokeWidth={1.5} /> Being arranged
            </span>
          )}
          {isReady && !delivered && (
            <span className="inline-flex items-center text-xs uppercase tracking-[0.15em] text-green-800 bg-green-50 px-3 py-1 border border-green-700/30" data-testid={`ytr-ready-${item.our_code}`}>Goods are Ready</span>
          )}
          {!preReady && !isReady && !delivered && (
            <span className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-[#8B7A3E]">
              <Clock className="w-3 h-3" strokeWidth={1.5} /> {STAGES[idx]?.label || ps}
            </span>
          )}
          <p className="text-sm text-[#5C5C5C] mt-2">₹{(item.subtotal || 0).toFixed(0)}</p>
        </div>
      </div>
      {preReady ? (
        <div className="border-t border-[#1A1A1A]/10 pt-4 text-xs text-[#8A8A8A]" data-testid={`ytr-pre-${item.our_code}`}>
          Our team is arranging this with the supplier. You&apos;ll get a WhatsApp update the moment it&apos;s ready.
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-1">
          {STAGES.map((s, i) => (
            <div key={s.key} className={`border-t-2 pt-2 ${i <= idx ? "border-[#C9A968]" : "border-[#E5E5E5]"}`}>
              <p className={`text-[10px] uppercase tracking-wide ${i <= idx ? "text-[#8B7A3E] font-medium" : "text-[#8A8A8A]"}`}>
                {s.label}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
